import { humanize } from 'common/helpers/humanize';
import { slugify } from 'common/utils/slug';

// Keyed on the backend's `location_types.name`, never on `location_types.id`: that id is a
// per-database serial and points at a different type in every environment. Do not re-key on
// the id. Mirrors `cloud/data/location_types.json`.
export const LOCATION_TYPE_KEYS = [
  'motor_free_end',
  'motor_drive_end',
  'fan_free_end',
  'fan_drive_end',
  'pump_free_end',
  'pump_drive_end',
  'compressor_free_end',
  'compressor_drive_end',
  'gearbox_input_end',
  'gearbox_output_end',
  'mill_free_end',
  'mill_drive_end',
  'conveyors_free_end',
  'conveyors_drive_end',
  'agitators_free_end',
  'screw_compressor_idle_shaft_drive_end',
  'screw_compressor_idle_shaft_free_end',
  'screw_compressor_input_shaft_free_end',
  'screw_compressor_input_shaft_drive_end',
  'engine_free_end',
  'engine_drive_end',
  'bearing_drive_end',
  'shaft_free_end',
  'shaft_drive_end',
  'turbine_free_end',
  'turbine_drive_end',
  'crusher_free_end',
  'crusher_drive_end',
  'steam_trap_inlet',
  'steam_trap_outlet',
  'tank_level',
  'input_shaft_free_end',
  'input_shaft_drive_end',
  'intermediate_shaft_free_end',
  'intermediate_shaft_drive_end',
  'output_shaft_drive_end',
  'output_shaft_free_end',
  'roll_free_end',
  'roll_drive_end',
  'pulley_left_side',
  'pulley_right_side',
  'building_temperature_location'
];

const LOCATION_TYPE_KEY_SET = new Set(LOCATION_TYPE_KEYS);

export function isLocationTypeKey(key) {
  return !!key && LOCATION_TYPE_KEY_SET.has(key);
}

/**
 * Canonical key for a location type named either the way the API sends it
 * (`motor_free_end`) or the way it is displayed (`Motor Free End`). Returns null for
 * user-defined location names, which have no translation and must stay verbatim.
 */
export function toLocationTypeKey(value) {
  const slug = slugify(value);
  return isLocationTypeKey(slug) ? slug : null;
}

/**
 * Canonical key for a `location_type_id`, resolved through the `available_location_types`
 * the metadata endpoint returns for this environment. Returns null when the id is not in
 * that list.
 *
 * @param {Array<{ text?: string, value?: number }>} [availableLocationTypes]
 */
export function locationTypeKeyFromId(locationTypeId, availableLocationTypes = []) {
  if (locationTypeId == null || locationTypeId === '') return null;
  const id = Number(locationTypeId);
  if (!Number.isFinite(id)) return null;

  const match = (availableLocationTypes || []).find(lt => Number(lt.value) === id);
  return match ? toLocationTypeKey(match.text) : null;
}

/**
 * Localized label for a location type. `fallbackText` covers user-defined locations and
 * types added to the backend before the locale files caught up.
 *
 * @param {function} t - i18next `t`
 * @param {string} [key] - canonical key from the helpers above
 * @param {string} [fallbackText] - raw name to humanize when there is no translation
 */
export function getLocationTypeLabel(t, key, fallbackText = '') {
  const fallback = humanize(fallbackText || key || '');
  if (!isLocationTypeKey(key)) return fallback;
  return t(`assets.options.locationTypes.${key}`, { defaultValue: fallback });
}

/** Localized label for a location type named the way the API sends it (`motor_free_end`). */
export function getLocationTypeLabelForName(t, name) {
  return getLocationTypeLabel(t, toLocationTypeKey(name), name);
}
